/**
 * Routes configuration based on Next.js pages structure
 * Replaces the old Module JSON configuration system
 */

import type { BaseMemberContextType } from "le-coffre-resources/dist/MemberTypes";

const routesPages = {
	Home: "/folders",
	DesignSystem: "/design-system",
	Login: "/login",
	CustomersLogin: "/customers/login",
	ThirdPartyLogin: "/third-party/login",
	ClientDashboard: {
		base: "/client-dashboard/[folderUid]",
		ViewDocuments: "/client-dashboard/[folderUid]/documentNotary/[documentUid]",
		ViewDocument: "/client-dashboard/[folderUid]/document/[documentUid]",
		ReceiveDocuments: "/client-dashboard/[folderUid]/received-documents",
		Account: "/client-dashboard/[folderUid]/account",
	},
	Folder: {
		base: "/folders",
		Shared: "/folders/shared",
		FolderInformation: "/folders/[folderUid]",
		CreateFolder: "/folders/create",
		Select: "/folders/select",
		SendDocuments: "/folders/[folderUid]/send-documents",
		AskDocument: "/folders/[folderUid]/[customerUid]/ask-documents",
		ViewDocuments: "/folders/[folderUid]/documents/[documentUid]",
		InvitedReceivedDocuments: "/folders/[folderUid]/invited/received-documents",
		EditDescription: "/folders/[folderUid]/edit/description",
		EditInformations: "/folders/[folderUid]/edit/informations",
		EditCollaborators: "/folders/[folderUid]/edit/collaborators",
		EditThirdParty: "/folders/[folderUid]/edit/third-parties/[thirdPartyUid]",
		EditSharedNotary: "/folders/[folderUid]/edit/shared-notaries/[sharedNotaryUid]",
		EditClient: "/folders/[folderUid]/edit/clients/[customerUid]",
		DocumentsReminderHistory: "/folders/[folderUid]/documents-reminder-history",
		AddClient: "/folders/[folderUid]/add/clients",
		FolderArchived: {
			base: "/folders/archived",
			FolderInformation: "/folders/archived/[folderUid]",
		},
	},
	MyAccount: "/my-account",
	OfficeDirectory: {
		clients: "/office-directory/clients",
		thirdParties: "/office-directory/third-parties",
		confreres: "/office-directory/confreres",
	},
	Collaborators: {
		base: "/collaborators",
		CollaboratorInformations: "/collaborators/[uid]",
	},
	Roles: {
		base: "/roles",
		RolesInformations: "/roles/[uid]",
		Create: "/roles/create",
	},
	DeedTypes: {
		base: "/deed-types",
		DeedTypesInformations: "/deed-types/[deedTypeUid]",
		Create: "/deed-types/create",
		Edit: "/deed-types/[deedTypeUid]/edit",
		Reminders: "/deed-types/reminders",
		RemindersInformations: "/deed-types/[deedTypeUid]/reminders",
		Cnil: "/deed-types/cnil",
		CnilInformations: "/deed-types/[deedTypeUid]/cnil",
	},
	Users: {
		base: "/users",
		UsersInformations: "/users/[uid]",
	},
	Offices: {
		base: "/offices",
		OfficesInformations: "/offices/[uid]",
	},
	DocumentTypes: {
		base: "/document-types",
		DocumentTypesInformations: "/document-types/[uid]",
		Create: "/document-types/create",
		Edit: "/document-types/[uid]/edit",
	},
	OfficesRib: "/offices/rib",
	Subscription: {
		base: "/subscription",
		New: "/subscription/new",
		Invite: "/subscription/invite",
		ManageCollaborators: "/subscription/manage-collaborators",
		Manage: {
			base: "/subscription/manage",
			Standard: "/subscription/manage/standard",
			Illimity: "/subscription/manage/illimity",
		},
		Error: "/subscription/error",
		Success: "/subscription/success",
		Subscribe: {
			base: "/subscription/subscribe",
			Standard: "/subscription/subscribe/standard",
			Illimity: "/subscription/subscribe/illimity",
		},
	},
	Notes: {
		base: "/notes",
		EditNote: "/notes/[noteUid]/edit",
		/** Participant cible : en pratique `customers.uid` (UserFolder). Pour un confrère hors ce flux, préférer `folder_sharing.uid` dans `[customerUid]` si ambiguïté bureau possible. */
		CreateNote: "/notes/folder/[folderUid]/customer/[customerUid]/create",
	},
	Legal: {
		base: "/legal",
		LegalInformations: "/legal/[legalUid]",
	},
	Admin: {
		Dashboard: "/admin/dashboard",
		OfficeSeats: "/admin/office-seats",
	},
	SuperAdmin: {
		SubscriptionPlans: "/super-admin/subscription-plans",
		SubscriptionPlansCreate: "/super-admin/subscription-plans/create",
	},
	NotFound: "/404",
};

export const Routes = {
	pages: routesPages,
};

/**
 * Helper function to replace route parameters
 */
export function buildRoute(route: string, params: Record<string, string>): string {
	let result = route;
	for (const [key, value] of Object.entries(params)) {
		result = result.replace(`[${key}]`, value);
	}
	return result;
}

export type ClientDashboardDocumentViewKind = "document" | "documentNotary";

/**
 * Client-space viewer paths. Never use `Routes.pages.Folder.ViewDocuments` (`/folders/...`)
 * from a customer or third-party dashboard — that page is the notary layout and will
 * call `/v1/notary/...` then tear down the client session on 401.
 */
export function buildClientDashboardDocumentViewPath(
	folderUid: string,
	documentUid: string,
	kind: ClientDashboardDocumentViewKind,
): string {
	const route =
		kind === "documentNotary" ? routesPages.ClientDashboard.ViewDocuments : routesPages.ClientDashboard.ViewDocument;
	return buildRoute(route, { folderUid, documentUid });
}

export type MemberDocumentViewerRole = BaseMemberContextType;

export function buildNotarySpaceDocumentViewPath(folderUid: string, documentUid: string): string {
	return buildRoute(routesPages.Folder.ViewDocuments, { folderUid, documentUid });
}

/**
 * Eye/view URL for a deposited or received document according to the viewer role.
 * Invited notaries stay on `/folders/...` (GUEST_FOLDERS JWT). Customer and third party stay on `/client-dashboard`.
 */
export function buildMemberSpaceDocumentViewPath(
	folderUid: string,
	documentUid: string,
	role: MemberDocumentViewerRole,
	kind: ClientDashboardDocumentViewKind,
): string {
	if (role === "invited_notary") {
		return buildNotarySpaceDocumentViewPath(folderUid, documentUid);
	}
	return buildClientDashboardDocumentViewPath(folderUid, documentUid, kind);
}
